import { z } from "zod";

import { taskStepPhaseSchema } from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";

/**
 * A human ruling on whether an automated sync step produced the correct result. The stable
 * `stepKey` identifies the customer-defined card, while `workflowStepKeys` captures the underlying
 * workflow steps represented by that card when the verdict was recorded.
 *
 * `phase` is what the card was asking when ops answered, which is what makes the verdict
 * measurable: `correct` in `qa` means automation was right; `correct` in `action_needed` or
 * `failed` means ops did or fixed the work. Null on verdicts recorded before it was captured.
 */
export const taskStepVerificationSchema = z.object({
  taskId: z.string().uuid(),
  stepKey: z.string().min(1),
  workflowStepKeys: z.array(z.string()),
  verdict: z.nativeEnum(VerificationResult),
  // Defaults so a response from a server that predates the column still parses during a deploy.
  phase: taskStepPhaseSchema.nullable().default(null),
  verifiedByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TaskStepVerification = z.infer<typeof taskStepVerificationSchema>;

export const upsertTaskStepVerificationSchema = z.object({
  stepKey: z.string().trim().min(1).max(100),
  workflowStepKeys: z.array(z.string().trim().min(1).max(100)).max(100).default([]),
  verdict: z.nativeEnum(VerificationResult),
  /** Optional only so clients built before it existed keep working through a deploy; current cards always send it. */
  phase: taskStepPhaseSchema.optional(),
});

export type UpsertTaskStepVerification = z.infer<typeof upsertTaskStepVerificationSchema>;

export const taskStepVerificationListSchema = z.object({
  verifications: z.array(taskStepVerificationSchema),
});
